/* rzavala
This service provides deployment status handling for all
local tenants
*/

import { Injector } from "@angular/core";
import { Http } from "@angular/http";
import {
    strEnum,
    SERVICE_URLS,
    TCI_APP_DEPLOYMENT_STAGE,
    TCI_TENANT_SERVICE_PROVIDER_TYPE,
    ITCIAppAutoScaleConfig,
    ITCIAppEnvControlProviderResponse,
    ITCIApplication,
    ITCIApplicationDeploymentProvider,
    ITCIApplicationDeployRequest,
    ITCIApplicationDeployResponse,
    ITCITenantComponentInput,
    ITCIApplicationDeploymentMiniDetails,
    TCIApplicationDeployConfirmationType,
    TCIApplicationDeploymentMode,
    TCIApplicationDeploymentNotification,
    TCIApplicationDeploymentStatus,
    TCIApplicationHybridStatusLabel,
    TCIAppStatusCategory,
    ITCIHybridInstanceStatus,
    TCITenantAppType
} from "@tci/common";
import { BaseTCIApplicationDeploymentProvider } from "../tci-tenant-services";
import { ITenantVPNServiceProvider } from "./tci-vpn-provider.service";

import { Observable, Observer, Subject, Subscription } from "rxjs";
import * as lodash from "lodash";
import * as LRUCache from "lru-cache";

export const HybridAppStatusType = strEnum(["healthy", "heartbeatLost", "stopped"]);
export type HybridAppStatusType = keyof typeof HybridAppStatusType;


export class TciTenantBaseDeploySharedService extends BaseTCIApplicationDeploymentProvider implements ITCIApplicationDeploymentProvider {
    private handlers: { [key: string]: boolean } = {};
    private appPushChangesHandlers: { [key: string]: boolean } = {};
    private status$: Subject<ITCIApplicationDeployResponse> = new Subject();

    // appStates: Stores the apps states to know when to publish an update
    appStates: { [key: string]: ITCIApplicationDeployResponse } = {}; // AppId is key

    // Monitor subscriptions collection
    private busySubscriptions: { [key: string]: Subscription[] } = {};

    // AppId is key
    // protected autoScaleConfig: { [key: string]: ITCIAppAutoScaleConfig } = {};
    protected autoScaleConfig: any = new LRUCache({ max: 500 });
    protected httpSvc: Http;

    /**
     * For on-premise apps
     */
    private onPremiseStatus$: Subject<ITCIApplicationDeployResponse> = new Subject();
    private onPremiseHandlers: { [key: string]: boolean } = {}; // To know what apps are being watched
    private onPremiseCheckWorker: { [key: string]: Subscription } = {};

    constructor(protected vpnProvider: ITenantVPNServiceProvider, injector: Injector, http: Http) {
        super(injector, http);
        this.httpSvc = http;
    }

    getServiceProviderType(): TCI_TENANT_SERVICE_PROVIDER_TYPE {
        return TCI_TENANT_SERVICE_PROVIDER_TYPE.APP_DEPLOY;
    }

    protected getVPNProvider(): ITenantVPNServiceProvider {
        return this.vpnProvider;
    }

    /**
     * Emmits a deployment status notification to UI
     * @param status Status data to be reflected at the UI
     * @param pushAppChangesMode False: From Tenant/Loop, True: From publishAppChanges
     */
    protected publish(status: ITCIApplicationDeployResponse, pushAppChangesMode = false) {
        if (this.handlers[status.appId]) {
            // Note: IPAS-13016, overwrite this.appStates value from status, but keep other values
            // because of other values will be used in tci-tenant-node-deploy.service.ts, canStartCheck()
            // this.appStates[status.appId] = status;
            lodash.assign(this.appStates[status.appId], status);

            if (!pushAppChangesMode) { // Publishing from the loop/tenant provider
                if (!this.appPushChangesHandlers[status.appId]) { // Avoid if publishAppChanges has not ended
                    this.status$.next(this.appStates[status.appId]);
                }
            } else { // Publishing from publishAppChanges
                this.status$.next(this.appStates[status.appId]);
            }

        } else if (this.onPremiseHandlers[status.appId]) {
            // Note: IPAS-13016, overwrite this.appStates value from status, but keep other values
            // because of other values will be used in tci-tenant-node-deploy.service.ts, canStartCheck()
            // this.appStates[status.appId] = status;
            if (lodash.isUndefined(this.appStates[status.appId])) {
                this.appStates[status.appId] = status;
            } else {
                lodash.assign(this.appStates[status.appId], status);
            }

            if (!pushAppChangesMode) { // Publishing from the loop/tenant provider
                // if (!this.appPushChangesHandlers[status.appId]) { // Avoid if publishAppChanges has not ended
                    this.onPremiseStatus$.next(this.appStates[status.appId]);
                // }
            } else { // Publishing from publishAppChanges
                this.onPremiseStatus$.next(this.appStates[status.appId]);
            }
        } else {
            console.error("No deployment tracking for appId", status.appId);
        }
    }

    /**
     * Tell TCI deployer UI whether to show confirmation popup
     * @param input
     */
    canShowConfirmationDialog(input: ITCIApplicationDeployRequest): Observable<ITCIApplicationDeployResponse> {
        const response: ITCIApplicationDeployResponse = {
            appId: input.appId,
            success: true,
            notificationType: null,
            confirmationType: TCIApplicationDeployConfirmationType.CONFIRM_DEFAULT
        };

        // No "live" concept in Scribe, so do not show dialog
        // Also, for TCI apps, the dialog is only when stopping all instances
        if (
            !input.tenantType.equals(TCITenantAppType.scribe) &&
            input.num_instances === 0 &&
            input.application.publishStage === TCI_APP_DEPLOYMENT_STAGE.PUBLISHED    // Check "live" status
        ) {
            response.confirmationType = TCIApplicationDeployConfirmationType.CONFIRM_IS_ALIVE;
        }
        return Observable.of(response);
    }

    /**
     * Show Start/Stop widgets from being displayed
     * @param input
     * @return @Observable<boolean>
     */
    canShowStartStop(input: ITCIApplicationDeployRequest): Observable<boolean> {
        return Observable.of(true);
    }

    canStart(input: ITCIApplicationDeployRequest): Observable<boolean> {
        return this.isPushingAppChanges(input);
    }

    start(input: ITCIApplicationDeployRequest): Observable<ITCIApplicationDeployResponse> {
        return Observable.of({
            notificationType: TCIApplicationDeploymentNotification.NOTIFICATION_REFRESH,
            status: null,
            appId: input.appId,
            success: true,
            failedInstances: 0,
            desiredInstances: 0,
            startingInstances: 0
        });
    }

    canStop(input: ITCIApplicationDeployRequest): Observable<boolean> {
        return this.isPushingAppChanges(input);
    }

    stop(input: ITCIApplicationDeployRequest): Observable<ITCIApplicationDeployResponse> {
        return Observable.of({
            result: true,
            notificationType: TCIApplicationDeploymentNotification.NOTIFICATION_REFRESH,
            status: null,
            appId: input.appId,
            success: true,
            failedInstances: 0,
            desiredInstances: 0,
            startingInstances: 0
        });
    }

    /**
     * Return state if the provider is busy waiting for the pushAppChanges to finish
     * @param input
     */
    isPushingAppChanges(input: ITCIApplicationDeployRequest): Observable<boolean> {
        return Observable.of(
            lodash.isUndefined(this.appPushChangesHandlers[input.appId]) ? false : this.appPushChangesHandlers[input.appId]
        );
    }

    /**
     * This is an explicit call to trigger a callback deployer action which is expected
     * to do something that changes the running instances behavior e.g. scaling up/down/stop/start
     * which may be triggered by AO api. This function is wrapping the AO call by waiting
     * for the App Status notification to be triggered via the refresh timer loop and
     * pass the completion status notifications back to the caller with callback
     * results and intermediate and end statuses.The Caller must subscribe for notifications.
     * @param input: @ITCIApplicationDeployRequest
     * @param callback: (cinput: @ITCIApplicationDeployRequest) => @Observable<@ITCIApplicationDeployResponse>
     * @return @Observable<@ITCIApplicationDeployResponse>
     */
    pushAppChanges(
        input: ITCIApplicationDeployRequest,
        callback: (cinput: ITCIApplicationDeployRequest) => Observable<ITCIApplicationDeployResponse>
    ): Observable<ITCIApplicationDeployResponse> {
        this.appPushChangesHandlers[input.appId] = true;
        return Observable.create((obs: Observer<ITCIApplicationDeployResponse>) => {
            // Note: do not show scaling status before execute callback
            // show scaling status in callback subscribe(), show finial status in callback finally()
            /*
            const newState: ITCIApplicationDeployResponse = {
                appId: input.appId, success: true,
                notificationType: TCIApplicationDeploymentNotification.NOTIFICATION_PROGRESS,
                status: new TCIApplicationDeploymentStatus("Scaling", TCIAppStatusCategory.IN_PROGRESS)
            };

            const state = lodash.assign(lodash.cloneDeep(this.appStates[input.appId] || {}), newState);
            this.publish(state, true);
            obs.next(state);
            */

            callback(input) // Execute the callback function
                .finally(() => {
                    this.appPushChangesHandlers[input.appId] = false;
                    const subject = input.application.remoteManagement ? this.onPremiseStatus$ : this.status$;
                    const subscription = subject // Subscribe to the core publishing awaiting for response
                        .filter(res => res.appId === input.appId)
                        .filter(res => res.notificationType === TCIApplicationDeploymentNotification.NOTIFICATION_REFRESH)
                        .subscribe(res => { // Final state
                            obs.next(res);
                            obs.complete();
                            subscription.unsubscribe();
                        });
                    this.refresh(input);
                })
                .subscribe(
                    cbRes => {
                        if (cbRes.notificationType === TCIApplicationDeploymentNotification.NOTIFICATION_RESULT_APP_CHANGES) {
                            // Note: app is scaling, change notificationType to progress
                            cbRes.notificationType = TCIApplicationDeploymentNotification.NOTIFICATION_PROGRESS;
                            this.publish(cbRes, true);
                        }
                        obs.next(cbRes); // Send callback response
                    },
                    error => obs.error(error)
                );
        });
    }

    /**
     * Return an observable indicating if the deployer is busy or not
     * The deployer is busy when pushingAppChanges/start/stop/scale is happening
     * and also when it gets a busy indication from polling async app manager
     * app status for tci apps and it is the responsibility of the tenant provider
     * to let the TCI infrastructure know when it is busy
     * @param input
     */
    isBusy(input: ITCIApplicationDeployRequest): Observable<boolean> {
        // we need to consider the appStates to indicate the tci app is
        // busy from remote async actions and local actions

        this.busySubscriptions[input.appId] = [];

        return Observable.create((obs: Observer<any>) => {
            this.busySubscriptions[input.appId].push( // Evaluate base method
                this.isPushingAppChanges(input)
                    .subscribe(res => obs.next(res))
            );

            // If the notification status is of refresh (completed) or status (error) it means it's not busy
            const evaluateNotificationType = (notification: TCIApplicationDeploymentNotification) => {
                return notification !== TCIApplicationDeploymentNotification.NOTIFICATION_REFRESH &&
                    notification !== TCIApplicationDeploymentNotification.NOTIFICATION_STATUS;
            };

            this.busySubscriptions[input.appId].push( // Subscribe to the deployment subject to monitor changes
                this.status$
                    .filter(sub => sub.appId === input.appId)
                    .subscribe(res => {
                        obs.next(evaluateNotificationType(res.notificationType));
                    })
            );

            const snapshot = this.getStatusSnapshot(input.application);
            if (snapshot) { // Publish initial state if a snapshot is available
                obs.next(evaluateNotificationType(snapshot.notificationType));
            }
        });
    }

    protected getAutoScaleConfig(input: ITCIApplicationDeployRequest): Observable<{} | ITCIAppAutoScaleConfig> {
        // if (!this.autoScaleConfig[input.appId]) {
        if (!this.autoScaleConfig.get(input.appId)) {
            return this.httpSvc.get(`${SERVICE_URLS.APPS}/${input.appId}/configuration?appType=${input.tenantType.toString()}`)
                .map(res => res.json())
                .map((res: ITCIAppEnvControlProviderResponse) => {
                    // this.autoScaleConfig[input.appId] = res.autoScaleConfig;
                    this.autoScaleConfig.set(input.appId, res.autoScaleConfig);
                    return res.autoScaleConfig;
                }).catch(err => {
                    console.warn("No response from autoscale checking endpoint", err);
                    // return Observable.of(null);
                    throw new Error("No response from autoscale checking endpoint" + err);
                });
        } else {
            return Observable.of(
                // this.autoScaleConfig[input.appId]
                this.autoScaleConfig.get(input.appId));
            // && String(this.autoScaleConfig[input.appId].autoScaleEnabled).trim().toLowerCase() === "true");
        }
    }

    /**
     * Return an observable<boolean> if the UI should behave in autoscaling mode
     * In this mode only the number of instances are to be captured and saved
     * in domain server for TCI apps and API orchestrator needs to turn on
     * auto scaling mode for existing / running apps. For Scribe there is no
     * Autoscaling and it should be false always
     * @param input
     */
    canAutoScale(input: ITCIApplicationDeployRequest): Observable<boolean> {
        // if (!this.autoScaleConfig[input.appId]) {
        if (!this.autoScaleConfig.get(input.appId)) {
            return this.getAutoScaleConfig(input)
                .map((res: ITCIAppAutoScaleConfig) => {
                    // this.autoScaleConfig[input.appId] = res.autoScaleConfig;
                    this.autoScaleConfig.set(input.appId, res);
                    return res && String(res.autoScaleEnabled).trim().toLowerCase() === "true";
                });
        } else {
            return Observable.of(
                // this.autoScaleConfig[input.appId]
                this.autoScaleConfig.get(input.appId)
                && String(this.autoScaleConfig.get(input.appId).autoScaleEnabled).trim().toLowerCase() === "true");
            // && String(this.autoScaleConfig[input.appId].autoScaleEnabled).trim().toLowerCase() === "true");
        }

    }

    /**
     * update auto scale configuration
     * @param input
     */
    updateAutoScale(input: ITCIApplicationDeployRequest): void {
        this.autoScaleConfig.set(input.appId, input.autoScaleConfig);
    }

    startStatusSubscription(app: ITCIApplication): Observable<boolean> {
        this.handlers[app.id] = true;
        delete this.appPushChangesHandlers[app.id];
        return Observable.of(true);
    }

    getStatusSnapshot(app: ITCIApplication): ITCIApplicationDeployResponse {
        return lodash.cloneDeep(this.appStates[app.id]) || null;
    }

    stopStatusSubscription(app: ITCIApplication): Observable<boolean> {
        this.handlers[app.id] = false;

        if (this.busySubscriptions[app.id]) { // Remove the monitor subscriptions
            this.busySubscriptions[app.id].forEach(subs => subs.unsubscribe());
        }

        // if (this.autoScaleConfig[app.id]) {
        //     delete this.autoScaleConfig[app.id];
        // }

        return Observable.of(true);
    }

    getStatusSubject(): Subject<ITCIApplicationDeployResponse> {
        return this.status$;
    }

    // Show/Hide scalebox in App Details
    canScale(input: ITCIApplicationDeployRequest): Observable<boolean> {
        if (lodash.isEqual(input.mode, TCIApplicationDeploymentMode.MODE_APP_LIST)) {
            return Observable.of(false);
        } else if (lodash.isEqual(input.mode, TCIApplicationDeploymentMode.MODE_APP_DETAILS)) {
            return Observable.of(!TCITenantAppType.scribe.equals(input.tenantType));
        } else {
            console.warn("canScale evaluation is not possible", input);
        }
    }

    setInstances(input: ITCIApplicationDeployRequest): Observable<ITCIApplicationDeployResponse> {
        return Observable.of({
            notificationType: TCIApplicationDeploymentNotification.NOTIFICATION_REFRESH,
            status: null,
            appId: input.appId,
            success: true,
            failedInstances: 0,
            desiredInstances: 0,
            startingInstances: 0
        });
    }

    getInstances(input: ITCIApplicationDeployRequest): Observable<ITCIApplicationDeployResponse> {
        if (input.application.onPremise) {
            return this.httpSvc.get(`${SERVICE_URLS.AGENT_DEPLOYER_STATUS}/${input.appId}?remoteManagement=${input.application.remoteManagement ? "true" : "false"}`)
                .map(res => res.json())
                .map(res => {
                    return {
                        notificationType: TCIApplicationDeploymentNotification.NOTIFICATION_REFRESH,
                        status: null,
                        appId: res.appId,
                        success: res.instances > 0,
                        instances: res.instances,
                        failedInstances: res.failedInstances,
                        desiredInstances: res.desiredInstances,
                        startingInstances: res.startingInstances
                    };
                });
        } else {
            return Observable.of(null);
        }
    }

    canShowDetails(input: ITCIApplicationDeployRequest): Observable<boolean> {
        return Observable.of(false);
    }

    getDetailsInput<T extends ITCITenantComponentInput>(input: ITCIApplicationDeployRequest): Observable<T> {
        return undefined;
    }

    /**
     * The details popup will no longer be a dynamic tenant component. The popup component will
     * display the HTML content returned by this function. The HTML content cannot have any forms
     * events, actions. Any resource links like images need to have absolute URLS resolved by the
     * tenant microservice static files. The input to this function is returned from the tenant
     * from the above function call getDetailsInput()
     * @param input
     */
    getDetailContent(input: ITCIApplicationDeployRequest): Observable<string | ITCIApplicationDeploymentMiniDetails> {
        return Observable.of("");
    }

    /**
     * The details popup overlay includes a button for the user to click and copy the content
     * into the clipboard. This method returns the text that would be copied.
     * The text should not include HTML tags.
     * @param input
     */
    getDetailTextForClipboard(input: ITCIApplicationDeployRequest): Observable<string> {
        return Observable.of("");
    }

    /**
     * Check if there is a status error to show the red dot badge on status.
     * @param input
     */
    showStatusError(input: ITCIApplicationDeployRequest): Observable<boolean> {
        return undefined;
    }

    /**
     * Check if an application is being currently handled
     * @param appId Application Id
     */
    protected isHandled(appId: string): boolean {
        return this.handlers[appId] === true;
    }

    /**
     * Get currently handled applications ids
     */
    protected getHandledAppIds(): string[] {
        return Object.keys(this.handlers).filter(id => this.handlers[id] === true);
    }

    refresh(input: ITCIApplicationDeployRequest): void {
    }

    /**
     * Returns the Subject that would emit the status of the application to
     * notify UI. The check loop would call "next" on the subject on status change
     * @param request
     */
    getOnPremiseAppStatusSubject(request: ITCIApplicationDeployRequest): Subject<ITCIApplicationDeployResponse> {
        return this.onPremiseStatus$;
    }

    /**
     * Returns the Subject that would emit the status of the application to
     * notify UI. The check loop would call "next" on the subject on status change
     * @param request
     */
     getOnPremiseApplicationStatusSubject(): Subject<ITCIApplicationDeployResponse> {
        return this.onPremiseStatus$;
    }

    /**
     * Before subscribing this method will be called to let the provider know
     * the consumer is subscribed. This base method starts the looping check.
     * @param app: @ITCIApplication
     * @return @Observable<boolean>
     */
    startOnPremiseStatusSubscription(app: ITCIApplication): Observable<boolean> {
        this.onPremiseHandlers[app.id] = true;

        // Cache status for this app
        let currentStatus: ITCIApplicationDeployResponse;

        // Initialize loop
        const requestFrequency = 10000;
        this.onPremiseCheckWorker[app.id] = Observable.timer(0, requestFrequency)
            .flatMap(() => this.getOnPremiseAppStatus(app))
            .subscribe(res => {
                if (res && res.success && (!currentStatus || !lodash.isEqual(currentStatus, res))) { // Publish only if different
                    currentStatus = res; // Update the status
                    this.appStates[app.id] = res;
                    this.onPremiseStatus$.next(res);
                }
            });

        return Observable.of(true);
    }

    /**
     * Before unsubscribing this method will be called to let the provider know
     * the consumer is unsubscribed. This base method stops the looping check.
     * @param app: @ITCIApplication
     * @return @Observable<boolean>
     */
    stopOnPremiseStatusSubscription(app: ITCIApplication): Observable<boolean> {
        this.onPremiseHandlers[app.id] = false;

        if (this.onPremiseCheckWorker[app.id]) {
            this.onPremiseCheckWorker[app.id].unsubscribe();
        }

        return Observable.of(true);
    }


    /**
     * Get on-premise apps status
     */
    private getOnPremiseAppStatus(app: ITCIApplication): Observable<ITCIApplicationDeployResponse> {
        const request: ITCIApplicationDeployRequest = {
            application: app,
            appId: app.id,
            tenantType: app.type,
            mode: null,
        };

        const responseForUi: ITCIApplicationDeployResponse = lodash.assign(request, {
            appId: request.appId,
            success: true,
            instances: 0,
            notificationType: TCIApplicationDeploymentNotification.NOTIFICATION_STATUS,
            status: new TCIApplicationDeploymentStatus(
                    TCIApplicationHybridStatusLabel.HEARTBEAT_FAILED.getString(), TCIAppStatusCategory.ERROR)
        });

        if (app.onPremise) {
            let url = `${SERVICE_URLS.AGENT_DEPLOYER_STATUS}/${app.id}`;
            if (app.remoteManagement) {
                url += "?remoteManagement=" + app.remoteManagement.toString().toLocaleLowerCase();
            } else {
                url += "?remoteManagement=false";
            }
            return this.httpSvc.get(url)
                .map(res => res.json())
                .catch(err => {
                    responseForUi.notificationType = TCIApplicationDeploymentNotification.NOTIFICATION_REFRESH;
                    responseForUi.status = new TCIApplicationDeploymentStatus(
                        TCIApplicationHybridStatusLabel.UNKNOWN.getString(), TCIAppStatusCategory.UNKNOWN);
                    return Observable.of(responseForUi);
                })
                .map((res: ITCIHybridInstanceStatus) => {
                    responseForUi.desiredInstances = res.desiredInstances || 0;
                    responseForUi.instances = res.instances || 0;
                    responseForUi.startingInstances = res.startingInstances || 0;
                    if (res.hybridAppStatus === HybridAppStatusType.healthy) {
                        if (res.instances !== res.desiredInstances) {
                            responseForUi.notificationType = TCIApplicationDeploymentNotification.NOTIFICATION_PROGRESS;
                                responseForUi.status = new TCIApplicationDeploymentStatus(
                                    (res.startingInstances > 0 ? "Scaling" : (res.desiredInstances === 0 ? "Stopping" : "Scaling")),
                                    TCIAppStatusCategory.IN_PROGRESS);
                        } else {
                            responseForUi.notificationType = TCIApplicationDeploymentNotification.NOTIFICATION_REFRESH;
                            responseForUi.status = new TCIApplicationDeploymentStatus(
                                TCIApplicationHybridStatusLabel.RUNNING.getString(), TCIAppStatusCategory.READY);
                        }
                    } else if (res.hybridAppStatus === HybridAppStatusType.stopped) {
                        if (res.instances > 0) {
                            responseForUi.notificationType = TCIApplicationDeploymentNotification.NOTIFICATION_REFRESH;
                            responseForUi.status = new TCIApplicationDeploymentStatus(
                                "Stopping", TCIAppStatusCategory.IN_PROGRESS);
                        } else {
                            responseForUi.notificationType = TCIApplicationDeploymentNotification.NOTIFICATION_REFRESH;
                            responseForUi.status = new TCIApplicationDeploymentStatus(
                                TCIApplicationHybridStatusLabel.STOPPED.getString(), TCIAppStatusCategory.NOT_READY);
                        }
                    }
                    return responseForUi;
                });
        } else {
            console.error("This application is not on-premise", app.id);
            responseForUi.success = false;
            return Observable.of(responseForUi);
        }
    }
}
